<template>
  <div class="discovery-list">
    <div class="discovery-types">
      <div 
        v-for="type in discoveryTypes" 
        :key="type.value"
        class="type-button"
        :class="{ active: selectedType === type.value }"
        @click="handleTypeSelect(type.value)"
      >
        <img v-if="type.icon" :src="type.icon" class="type-icon" />
        {{ type.label }}
      </div>
    </div>
    
    <div class="discoveries">
      <div v-if="filteredDiscoveries.length === 0" class="no-records">
        尚無相關記錄
      </div>
      <DiscoveryItem
        v-for="(discovery, index) in filteredDiscoveries" 
        :key="discovery.id || index"
        :discovery="discovery"
        @click="handleDiscoveryClick"
      />
    </div>
  </div>
</template>

<script>
import DiscoveryItem from './DiscoveryItem.vue';

/**
 * Discovery List component following SOLID principles
 * Single Responsibility: Manages discovery filtering and display
 * Interface Segregation: Clean props interface
 */
export default {
  name: 'DiscoveryList',
  
  components: {
    DiscoveryItem
  },
  
  props: {
    discoveries: {
      type: Array,
      default: () => []
    },
    filteredDiscoveries: {
      type: Array,
      default: () => []
    },
    discoveryTypes: {
      type: Array,
      default: () => []
    },
    selectedType: {
      type: String,
      default: 'all'
    }
  },
  
  emits: [
    'type-selected',
    'discovery-clicked'
  ],
  
  methods: {
    /**
     * Handle discovery type selection
     * @param {string} type - Selected type
     */
    handleTypeSelect(type) {
      this.$emit('type-selected', type);
    },
    
    /**
     * Handle discovery item click
     * @param {Object} discovery - Discovery object
     */
    handleDiscoveryClick(discovery) {
      this.$emit('discovery-clicked', discovery);
    }
  }
}
</script>

<style lang="scss" scoped>
.discovery-list {
  padding: 20px;
  
  .discovery-types {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 12px;
    
    .type-button {
      display: flex;
      align-items: center;
      padding: 8px 16px;
      background-color: rgba(255, 255, 255, 0.7);
      border-radius: 20px;
      cursor: pointer;
      font-weight: 500;
      transition: all 0.3s ease;
      
      .type-icon {
        width: 20px;
        height: 20px;
        margin-right: 8px;
      }
      
      &.active {
        background-color: #b6d765;
        color: #333;
      }
      
      &:hover {
        background-color: rgba(182, 215, 101, 0.7);
      }
    }
  }
  
  .discoveries {
    display: flex;
    flex-direction: column;
    gap: 15px;
    max-height: 50vh;
    overflow-y: auto;
    padding-right: 5px;

    // Custom scrollbar
    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.1);
      border-radius: 3px;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.3);
      border-radius: 3px;
    }

    .no-records {
      text-align: center;
      padding: 30px;
      font-size: 16px;
      color: #666;
      background-color: rgba(255, 255, 255, 0.7);
      border-radius: 8px;
    }
    
    .discovery-item {
      background-color: rgba(255, 255, 255, 0.8);
      border-radius: 8px;
      padding: 15px;
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
      
      &.missing {
        background-color: rgba(255, 200, 200, 0.8);
        border-left: 4px solid #ff6b6b;
      }
      
      &.failed {
        background-color: rgba(255, 180, 180, 0.8);
        border-left: 4px solid #ff4040;
      }
      
      .discovery-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;
        
        .discovery-type-info {
          display: flex;
          align-items: center;
          background-color: rgba(0, 0, 0, 0.05);
          padding: 4px 10px;
          border-radius: 15px;
          
          .discovery-type-icon {
            width: 20px;
            height: 20px;
            margin-right: 6px;
          }
          
          .discovery-type-label {
            font-size: 14px;
            color: #555;
          }
        }
        
        .discovery-member {
          font-size: 14px;
          color: #666;
          text-align: right;
        }
      }
      
      .discovery-name {
        text-align: left;
        margin-bottom: 10px;
        
        .status-badge {
          display: inline-block;
          font-size: 12px;
          font-weight: normal;
          background-color: #ff6b6b;
          color: white;
          padding: 2px 8px;
          border-radius: 10px;
          margin-bottom: 5px;
          
          &.failed-badge {
            background-color: #ff4040;
          }
        }
        
        .name-text {
          font-size: 18px;
          font-weight: 600;
          word-break: break-word;
        }
      }
      
      .missing-message {
        background-color: rgba(255, 255, 255, 0.5);
        padding: 10px;
        border-radius: 6px;
        font-size: 14px;
        color: #d32f2f;
        margin-bottom: 10px;
        font-style: italic;
      }
      
      .unlockables {
        margin-top: 10px;
        padding-top: 10px;
        border-top: 1px dashed #ddd;
        
        .unlockables-title {
          font-weight: 500;
          margin-bottom: 5px;
          font-size: 14px;
          color: #666;
        }
        
        .unlockable-item {
          padding: 5px 0;
          display: flex;
          align-items: center;
          
          .unlockable-icon {
            width: 16px;
            height: 16px;
            margin-right: 6px;
          }
          
          .unlockable-type {
            color: #666;
            margin-right: 5px;
          }
          
          .unlockable-name {
            font-weight: 500;
          }
        }
      }
    }
  }
}
</style>
